import React, { useMemo, useState } from "react";
import { useHistory, useLocation, Route, Redirect, Switch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FmIcon, FmStepper } from "../../Components";
import { LOGGED_IN_ROUTES } from "../../Constants";
import { useTheme, useIsMobile } from "../../Hooks";
import { FmMobileStepper } from "../../Components/FmStepper/FmMobileStepper";
import CropStagePage from "./CropStage";
import SoilStagePage from "./SoilStage";
import ResultsStagePage from "./ResultsStage";
import FarmStagePage from "./FarmStage";
import CustomizeStagePage from "./CustomizeStage";
import useStyles from "./MulticoteAgriSolutionPage.style";
import { useDispatch, useSelector } from "react-redux";
import { AgriSolutionState, GeneralState } from "../../Redux";
import { PageReleaseChart } from "../PageReleaseChart";
import StepTwoPage from "../MulticoteSolution/StepTwoPage";

const MulticoteAgriSolutionPage = () => {
    const [expended, setExpanded] = useState<boolean>(false);

    const classes = useStyles();
    const { t } = useTranslation();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useIsMobile();
    const history = useHistory();
    const dispatch = useDispatch();

    dispatch(GeneralState.Actions.setUserIsNavigateToMainPage(true));

    const { soilStateCanBeNavigated, resultsStateCanBeNavigated, farmStateCanBeNavigated, customizeStateCanBeNavigated } = useSelector(
        AgriSolutionState.Selectors.selectStepsAvailability
    );

    const activeStepIndex = useMemo(() => {
        let activeIndex = 0;
        switch (location?.pathname || "") {
            case LOGGED_IN_ROUTES.AGRI_SOLUTION_FIRST_STEP:
                activeIndex = 0;
                dispatch(AgriSolutionState.Actions.setStepIndexPage(0));
                break;
            case LOGGED_IN_ROUTES.AGRI_SOLUTION_SECOND_STEP:
                activeIndex = 1;
                dispatch(AgriSolutionState.Actions.setStepIndexPage(1));
                break;
            case LOGGED_IN_ROUTES.AGRI_SOLUTION_THIRD_STEP:
                activeIndex = 2;
                dispatch(AgriSolutionState.Actions.setStepIndexPage(2));
                break;
            case LOGGED_IN_ROUTES.AGRI_SOLUTION_RELEASE_CHART:
                activeIndex = 2.5;
                dispatch(AgriSolutionState.Actions.setStepIndexPage(2.5));
                break;
            case LOGGED_IN_ROUTES.AGRI_SOLUTION_FORTH_STEP:
                activeIndex = 3;
                dispatch(AgriSolutionState.Actions.setStepIndexPage(3));
                break;
            case LOGGED_IN_ROUTES.AGRI_SOLUTION_FIFTH_STEP:
                activeIndex = 4;
                dispatch(AgriSolutionState.Actions.setStepIndexPage(4));
                break;
            default:
                console.log("switch default", location);
                break;
        }
        return activeIndex;
    }, [location]);

    const steps = useMemo(() => {
        return [
            {
                desktopLabel: t("steps.cropsDesktop"),
                mobileLabel: t("steps.cropsMobile"),
                icon: (
                    <FmIcon
                        className={classes.stepImage}
                        url={activeStepIndex === 0 ? theme.images.stepCropActive : theme.images.stepCrop}
                        title={t("steps.cropsDesktop")}
                    />
                ),
            },
            {
                desktopLabel: t("steps.soilDesktop"),
                mobileLabel: t("steps.soilMobile"),
                icon: (
                    <FmIcon
                        className={classes.stepImage}
                        url={activeStepIndex === 1 ? theme.images.stepSoilActive : theme.images.stepSoil}
                        title={t("steps.soilDesktop")}
                    />
                ),
            },
            {
                desktopLabel: t("steps.resultsDesktop"),
                mobileLabel: t("steps.resultsMobile"),
                icon: (
                    <FmIcon
                        className={activeStepIndex === 2 ? classes.stepImageSmall : "stepScaler"}
                        url={activeStepIndex === 2 ? theme.images.stepResultActive : theme.images.stepWithCO2}
                        title={t("steps.resultsDesktop")}
                    />
                ),
            },
            {
                desktopLabel: t("steps.farmDesktop"),
                mobileLabel: t("steps.farmMobile"),
                icon: (
                    <FmIcon
                        className={classes.stepImage}
                        url={activeStepIndex === 3 ? theme.images.stepFarmActive : theme.images.stepFarm}
                        title={t("steps.farmDesktop")}
                    />
                ),
            },
            {
                desktopLabel: t("steps.customizeDesktop"),
                mobileLabel: t("steps.customizeMobile"),
                icon: (
                    <FmIcon
                        className={classes.stepImage}
                        url={activeStepIndex === 4 ? theme.images.stepCustomizeActive : theme.images.stepCustomize}
                        title={t("steps.customizeDesktop")}
                    />
                ),
            },
        ];
    }, [activeStepIndex, t]);

    const handleExpandClick = () => {
        setExpanded((prev: any) => !prev);
    };

    const handleStepperItemClick = (newIndex: number) => {
        if (newIndex !== activeStepIndex) {
            if (newIndex === 0) {
                handleExpandClick();
                history.push(LOGGED_IN_ROUTES.AGRI_SOLUTION_FIRST_STEP);
            } else if (newIndex === 1 && soilStateCanBeNavigated) {
                handleExpandClick();
                history.push(LOGGED_IN_ROUTES.AGRI_SOLUTION_SECOND_STEP);
            } else if (newIndex === 2 && resultsStateCanBeNavigated) {
                handleExpandClick();
                history.push(LOGGED_IN_ROUTES.AGRI_SOLUTION_THIRD_STEP);
            } else if (newIndex === 3 && farmStateCanBeNavigated) {
                handleExpandClick();
                history.push(LOGGED_IN_ROUTES.AGRI_SOLUTION_FORTH_STEP);
            } else if (newIndex === 4 && customizeStateCanBeNavigated) {
                handleExpandClick();
                history.push(LOGGED_IN_ROUTES.AGRI_SOLUTION_FIFTH_STEP);
            }
        }
    };
    return (
        <div className={classes.container}>
            <div className={classes.stepperContainer}>
                {isMobile && (
                    <FmMobileStepper
                        data={steps}
                        activeItemIndex={activeStepIndex}
                        onItemSelect={handleStepperItemClick}
                        handleExpandClick={handleExpandClick}
                        expended={expended}
                    />
                )}
                {!isMobile && <FmStepper data={steps} activeItemIndex={activeStepIndex} onItemSelect={handleStepperItemClick} />}
            </div>
            <div className={classes.content}>
                <Switch>
                    <Route path={LOGGED_IN_ROUTES.AGRI_SOLUTION_FIRST_STEP}>
                        <CropStagePage />
                    </Route>
                    {soilStateCanBeNavigated && (
                        <Route path={LOGGED_IN_ROUTES.AGRI_SOLUTION_SECOND_STEP}>
                            <SoilStagePage />
                        </Route>
                    )}
                    {resultsStateCanBeNavigated && (
                        <Route path={LOGGED_IN_ROUTES.AGRI_SOLUTION_THIRD_STEP}>
                            <ResultsStagePage />
                        </Route>
                    )}
                    {farmStateCanBeNavigated && (
                        <Route path={LOGGED_IN_ROUTES.AGRI_SOLUTION_FORTH_STEP}>
                            <FarmStagePage />
                        </Route>
                    )}
                    {customizeStateCanBeNavigated && (
                        <Route path={LOGGED_IN_ROUTES.AGRI_SOLUTION_FIFTH_STEP}>
                            <CustomizeStagePage />
                        </Route>
                    )}
                    <Route path={LOGGED_IN_ROUTES.AGRI_SOLUTION_RELEASE_CHART}>
                        <PageReleaseChart />
                    </Route>
                    <Route path={LOGGED_IN_ROUTES.SOLUTION_SECOND_STEP}>
                        <StepTwoPage />
                    </Route>
                    <Redirect from='/' to={LOGGED_IN_ROUTES.AGRI_SOLUTION_FIRST_STEP} />
                </Switch>
            </div>
        </div>
    );
};

export default MulticoteAgriSolutionPage;
